import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useToast } from "../../../contexts/ToastContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  generateReport,
  listClients,
  listPlaylists,
  listScreens,
} from "../../../lib/dashboardApi";
import type { ReportFilters } from "../../../types/dashboard";

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

export function Reports() {
  const { toast } = useToast();
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: listClients });
  const { data: playlists = [] } = useQuery({ queryKey: ["playlists"], queryFn: listPlaylists });
  const { data: screensData } = useQuery({
    queryKey: ["screens", { page: 1, per_page: 500 }],
    queryFn: () => listScreens({ page: 1, per_page: 500 }),
  });
  const screens = screensData?.screens ?? [];

  const [filters, setFilters] = useState<ReportFilters>({
    range_type: "weekly",
    date_from: TODAY_ISO(),
    date_to: TODAY_ISO(),
    client_id: "",
    playlist_id: "",
    screen_id: "",
    breakdown_daily: false,
    breakdown_screen: false,
    breakdown_location: false,
  });
  const [busy, setBusy] = useState(false);

  function update<K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  async function generate() {
    setBusy(true);
    try {
      const blob = await generateReport(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `seilan-report-${TODAY_ISO()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Report downloaded.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not generate report."), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="page-title">Reports</h2>

      <div className="report-hero">
        <div className="report-hero-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </div>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Generate a PDF Report</h3>
          <p style={{ color: "var(--text2)", fontSize: 13 }}>
            Build a professional report showing how long your ads played, on which screens, and where.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="report-section-label">
          <span className="report-step">1</span> Time Period
        </div>
        <p className="report-help">What date range should the report cover?</p>
        <div className="form-group">
          <select
            className="form-input"
            value={filters.range_type}
            onChange={(e) => update("range_type", e.target.value as ReportFilters["range_type"])}
          >
            <option value="daily">Yesterday</option>
            <option value="weekly">Last 7 days</option>
            <option value="monthly">Last 30 days</option>
            <option value="custom">Pick specific dates</option>
          </select>
        </div>
        {filters.range_type === "custom" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <input
              type="date"
              className="form-input"
              style={{ width: 160 }}
              value={filters.date_from ?? ""}
              onChange={(e) => update("date_from", e.target.value)}
            />
            <span style={{ color: "var(--text2)", fontSize: 13 }}>to</span>
            <input
              type="date"
              className="form-input"
              style={{ width: 160 }}
              value={filters.date_to ?? ""}
              onChange={(e) => update("date_to", e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="card">
        <div className="report-section-label">
          <span className="report-step">2</span> Filter{" "}
          <span className="report-optional">(optional)</span>
        </div>
        <p className="report-help">Narrow down to a specific group, playlist, or screen.</p>
        <div className="report-filter-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Screen Group</label>
            <select
              className="form-input"
              value={filters.client_id ?? ""}
              onChange={(e) => update("client_id", e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">All groups</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Playlist</label>
            <select
              className="form-input"
              value={filters.playlist_id ?? ""}
              onChange={(e) =>
                update("playlist_id", e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">All playlists</option>
              {playlists.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Screen</label>
            <select
              className="form-input"
              value={filters.screen_id ?? ""}
              onChange={(e) => update("screen_id", e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">All screens</option>
              {screens.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.player_id ? ` (${s.player_id})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="report-section-label">
          <span className="report-step">3</span> Detail Tables{" "}
          <span className="report-optional">(optional)</span>
        </div>
        <p className="report-help">Add extra tables. The summary is always included.</p>
        <div className="report-checkbox-row">
          {(
            [
              ["breakdown_daily", "Day-by-day", "Total play time and active screens per day"],
              ["breakdown_screen", "Per screen", "How much each screen played"],
              ["breakdown_location", "Per location", "Groups screens by location"],
            ] as const
          ).map(([key, title, sub]) => (
            <label key={key} className="report-check">
              <input
                type="checkbox"
                checked={filters[key]}
                onChange={(e) => update(key, e.target.checked)}
              />
              <span className="report-check-box">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span>
                <strong>{title}</strong>
                <small>{sub}</small>
              </span>
            </label>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary btn-report-generate"
        onClick={generate}
        disabled={busy}
      >
        {busy ? "Generating…" : "Generate & Download PDF"}
      </button>
    </>
  );
}
